import React, { useState, useEffect, useLayoutEffect } from 'react';

// Hooks
import { useSelector } from 'react-redux';
import usePaymentFailureGuard from '~/hooks/usePaymentFailureGuard';

// libraries
import { Outlet } from 'react-router-dom';

// Components
import Header from './Header';
import SideBarNew from './SideBarNew';
import SideBar from './SideBar';

// Utils
import { getImpersonationMode } from '~/utils/auth';
import { isOfferGeneratorWebsite } from '~/utils/helperFunctions';

// Mui Components
import { Box } from '@mui/material';

const Layout = (props) => {
  const {
    isSideBar,
    isAdminMode,
    onClick,
    isWidgetVisible,
    setIsWidgetVisible,
    hideHeader = false,
  } = props;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const isSettingsSidebarLayout = isSideBar && !window.location.pathname.includes('orders');
  const settingsShellEdge = 'var(--app-shell-gutter, 24px)';
  const settingsSidebarWidth = '292px';
  const settingsCollapsedSidebarWidth = '60px';

  // Route guard: redirect to /account/wallet when payment failure modal is active
  usePaymentFailureGuard();

  const isImpersonatedMode = getImpersonationMode() || false;
  const currentMode = useSelector((state) =>
    state.userReducers.user.isLiveMode === true
      ? 'Live'
      : state.userReducers.user.isLiveMode === false
        ? 'Test'
        : null
  );

  // Listen for sidebar state changes
  useEffect(() => {
    const handleSidebarStateChange = (event) => {
      if (event.detail && typeof event.detail === 'object') {
        setSidebarCollapsed(event.detail.collapsed || false);
        setSidebarExpanded(event.detail.expanded || false);
      }
    };

    window.addEventListener('sidebarStateChange', handleSidebarStateChange);
    return () => window.removeEventListener('sidebarStateChange', handleSidebarStateChange);
  }, []);

  // Calculate content margin based on sidebar state
  const getContentMargin = () => {
    if (isSettingsSidebarLayout && window.innerWidth <= 900) {
      return `calc(${settingsShellEdge} + ${settingsCollapsedSidebarWidth})`;
    }

    if (window.innerWidth <= 900) {
      return sidebarCollapsed && !sidebarExpanded ? '60px' : sidebarExpanded ? '60px' : '280px';
    }
    return '280px';
  };

  useLayoutEffect(() => {
    if (window.innerWidth <= 900) {
      setSidebarCollapsed(true);
      setSidebarExpanded(false);
    }
  }, [sidebarCollapsed, sidebarExpanded]);

  return (
    <>
      {!hideHeader && (
        <Header
          isAdminMode={isAdminMode}
          onClick={onClick}
          isWidgetVisible={isWidgetVisible}
          setIsWidgetVisible={setIsWidgetVisible}
        />
      )}
      <div
        className={
          isOfferGeneratorWebsite()
            ? ``
            : isImpersonatedMode
              ? 'dashboard-body impersonate'
              : 'dashboard-body'
        }
        id="dashboard"
      >
        <>
          <Box className="overlay" />
          <>
            {isSideBar && !window.location.pathname.includes('orders') && (
              <SideBarNew currentMode={currentMode} isImpersonatedMode={isImpersonatedMode} />
            )}
            {isSideBar && window.location.pathname.includes('orders') && (
              <SideBar isSidebarOpen={true} name="order" isImpersonatedMode={isImpersonatedMode} />
            )}
            <Box
              className={`mainContent ${!isAdminMode ? 'userMainContent' : ''} ${isSettingsSidebarLayout ? 'settingsMainContent' : ''}`}
              sx={{
                '--page-shell-gutter': isSettingsSidebarLayout
                  ? 'var(--settings-content-gap)'
                  : undefined,
                marginLeft: getContentMargin(),
                marginRight: 0,
                // minHeight: 'calc(100vh - 120px)',
                '@media (max-width: 900px)': {
                  marginLeft: isSettingsSidebarLayout
                    ? `calc(${settingsShellEdge} + ${settingsCollapsedSidebarWidth})`
                    : !isSideBar
                      ? '0px'
                      : sidebarCollapsed && !sidebarExpanded
                        ? '60px'
                        : sidebarExpanded
                          ? '60px'
                          : '280px',
                  marginRight: isSettingsSidebarLayout ? settingsShellEdge : '0px',
                },
                '@media (min-width: 901px)': {
                  marginLeft:
                    isSideBar && window.location.pathname.includes('orders')
                      ? '200px'
                      : isSideBar
                        ? `calc(${settingsShellEdge} + ${settingsSidebarWidth})`
                        : '0px',
                  marginRight: isSettingsSidebarLayout ? settingsShellEdge : '0px',
                },
              }}
            >
              <Outlet />
            </Box>
          </>
        </>
      </div>
    </>
  );
};

export default Layout;
